/** DonutChart — a ring split by segments with a centre total (cost-of-production
 *  breakdown), on Recharts like the rest of the kit.
 *
 *  `paddingAngle` does the separating, not a stroke around each arc: a stroke adds ink
 *  that is not data, and on a five-slot ring it turns the mix into a stained-glass
 *  window. A recessive track behind the arcs closes the ring, so a mix that does not
 *  sum to the whole shows as a gap rather than passing as a rounding error.
 *
 *  The centre total is HTML over the chart, not SVG <text>: it is the largest number on
 *  the card and it should wear the app's own type tokens, which SVG text cannot inherit. */
import { Cell, Pie, PieChart, Tooltip } from 'recharts'
import { useTokens } from './useTokens'

interface Segment {
  label: string
  value: number
  color: string
}
interface Props {
  segments: Segment[]
  centerLabel: string
  centerSub: string
  size?: number
}

const RING = 15

export default function DonutChart({ segments, centerLabel, centerSub, size = 158 }: Props) {
  const t = useTokens()
  const outer = size / 2 - 6
  const inner = outer - RING
  const total = segments.reduce((a, s) => a + s.value, 0) || 1
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <PieChart width={size} height={size}>
        <Pie data={[{ value: 1 }]} dataKey="value" cx="50%" cy="50%" innerRadius={inner} outerRadius={outer}
          fill={t.line2} stroke="none" isAnimationActive={false} />
        <Pie data={segments} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={inner} outerRadius={outer}
          paddingAngle={1.4} stroke="none" isAnimationActive={false}>
          {segments.map((s) => <Cell key={s.label} fill={s.color} />)}
        </Pie>
        <Tooltip
          formatter={(v: number, name: string) => [`${((v / total) * 100).toFixed(1)}%`, name]}
          contentStyle={{
            background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 8,
            fontSize: 11, color: 'var(--ink)', boxShadow: 'var(--shadow)',
          }}
        />
      </PieChart>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[19px] font-bold tracking-[-.01em] text-ink">{centerLabel}</span>
        <span className="mt-0.5 font-mono text-[8px] tracking-[.1em] text-mut2">{centerSub}</span>
      </div>
    </div>
  )
}
